import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Permission } from '@/lib/api/domains/permissions';
import {
  permissionCatalogQuery,
  permissionSetCompositionAsOfQuery,
} from '../api/permission-queries';

/*
 * The permission-set AS-OF-DATE HISTORY surface (F03 R48; seam F69). Built for
 * task 6.2 against the task-6.1 tests in PermissionSetHistory.test.tsx:
 *
 *   - AC-b8ffb235 (R48) — THE system SHALL let an administrator open a
 *     permission set and see what it contained ON A STATED EARLIER DATE. The
 *     surface takes a date, reads the set's point-in-time composition for that
 *     date (`permissionSetCompositionAsOfQuery`) and shows exactly the
 *     permissions the set held then — resolved for display the way the list and
 *     composer resolve the current composition (each permission as its
 *     `action`, against `permissionCatalogQuery`). An empty point-in-time read
 *     is said plainly to have contained nothing (a valid read, not a missing
 *     one).
 *   - AC-779bdd64 (F03 rollup, tpa half) — SURFACE the as-of-date read (seam
 *     F69). Choosing a DIFFERENT earlier date re-reads the point-in-time
 *     composition for that date (keyed per set AND per date) and shows what the
 *     set contained then — not a single frozen snapshot. The stated date is
 *     reflected back so the reader knows which date they are viewing. Keeping
 *     set contents before every change is Identity's; surfacing the read is
 *     this surface's.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CR-E01-15 ASSUMPTION (confirm bucket — DEPENDS, not BLOCKED).
 *
 * This surface is built on the ASSUMPTION that Identity records permission-set
 * membership before/after every change and RETURNS a set's composition as of a
 * stated earlier date (a point-in-time read). The capability is ASSUMED to
 * exist and must be RE-CHECKED against Identity's running RBAC/history API — it
 * is not built from scratch here. AC-779bdd64 and AC-b8ffb235 carry
 * `[DEPENDS — CR-E01-15]`; the identity half is Identity's. The full assumption
 * note and exactly WHAT TO RE-CHECK if CR-E01-15 is not confirmed as assumed
 * (that Identity exposes a point-in-time read at all; the `asOf` date
 * semantics; the field the resolved date echoes back on) live beside the
 * transport, `lib/api/domains/permissions` `PermissionSetCompositionAsOf`. If
 * any of those differs, this surface, its query, its MSW stand-in and the
 * transport are regenerated against Identity's confirmed contract (workflow
 * rules 1, 7); the frontend builds NO TPA-side substitute for an Identity gap.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * F03 records; F03b refuses — this surface READS and DISPLAYS Identity's
 * point-in-time composition. It is read-only (F69 history is read-only) and
 * makes no authorization decision. A date carries no PII, so it is safe to show
 * and to put on the request; the set id is Identity's opaque id, never a name
 * (no PII in URLs/telemetry).
 */

/** A complete calendar date (`YYYY-MM-DD`) the point-in-time read fires for. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The actions of a point-in-time composition, resolved against the catalog —
 * the SAME resolution the list and composer use, so a permission reads as its
 * `action` wherever it is shown (R48, R52). An id with no catalog entry is
 * dropped rather than shown as a bare id.
 */
function resolveActions(
  permissionIds: string[],
  catalogById: Map<string, Permission>,
): string[] {
  return permissionIds
    .map((id) => catalogById.get(id)?.action)
    .filter((action): action is string => action !== undefined);
}

export function PermissionSetHistory({
  setId,
}: {
  /** The set whose history is being read (Identity's opaque id, never a name). */
  setId: string;
}): React.ReactElement {
  const dateFieldId = useId();

  // The stated earlier date. Held as the raw `YYYY-MM-DD` string the field
  // yields; the read fires only once it is a complete calendar date (the
  // `enabled` guard below), and re-fires when the date changes — never a single
  // frozen snapshot (AC-779bdd64).
  const [asOf, setAsOf] = useState('');
  const hasDate = CALENDAR_DATE.test(asOf);

  // The point-in-time composition for the stated date (AC-b8ffb235). Keyed per
  // set AND per date, so each chosen date is read and cached independently;
  // `enabled` holds the query until a complete date is stated, and changing the
  // date changes the key, so the surface re-reads per chosen date (AC-779bdd64)
  // and never shows a stale date's composition.
  const { data: composition } = useQuery({
    ...permissionSetCompositionAsOfQuery(setId, asOf),
    enabled: hasDate,
  });

  // The catalog that resolves each permission id to its display text — the same
  // read the list and composer use (R52), so the three surfaces agree on how a
  // permission reads (its `action`).
  const { data: catalogData } = useQuery(permissionCatalogQuery());

  // Index the catalog by id so a composition resolves in one pass. Memoised
  // because the lookup is rebuilt only when the catalog changes, not on every
  // render as the stated date changes — the React Compiler cannot see that the
  // Map identity should be stable across unrelated renders (same reasoning as
  // PermissionSetList).
  const catalogById = useMemo(() => {
    const index = new Map<string, Permission>();
    for (const permission of catalogData ?? []) {
      index.set(permission.id, permission);
    }
    return index;
  }, [catalogData]);

  // The permissions the set held AS OF the stated date, resolved for display.
  // Shown only once a date is stated and its composition is in hand; an empty
  // array is a set that contained nothing then (a valid read — AC-b8ffb235),
  // said plainly below.
  const actions =
    hasDate && composition
      ? resolveActions(composition.permissionIds, catalogById)
      : [];
  const showEmpty =
    hasDate && composition !== undefined && actions.length === 0;

  return (
    <section
      aria-label="Permission set history"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor={dateFieldId}>Date</Label>
        <Input
          id={dateFieldId}
          type="date"
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
        />
      </div>

      {/* Reflect the stated date back so the reader knows which date's
          composition they are viewing (AC-779bdd64) — "what did this set
          contain on <date>" is answerable from the surface itself. A date
          carries no PII. */}
      {hasDate ? <p className="text-sm font-medium">As of {asOf}</p> : null}

      {/* The permissions the set held on the stated date, each as its action —
          exactly what the set contained THEN (AC-b8ffb235), resolved the way
          the list and composer resolve the current composition. */}
      {actions.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {actions.map((action, index) => (
            <li
              // The action text is not unique across sets; the index pins the
              // position within this read's own list.
              key={`${asOf}-${index}`}
              className="text-sm"
            >
              {action}
            </li>
          ))}
        </ul>
      ) : null}

      {/* An empty point-in-time read: the set contained nothing as of that date
          (AC-b8ffb235). A valid read, said plainly — not a missing one. */}
      {showEmpty ? (
        <p className="text-muted-foreground text-sm">
          This set contained nothing on that date.
        </p>
      ) : null}
    </section>
  );
}
